import ContentIllustration from '../../../components/ui/ContentIllustration';
import { useState, useMemo, useEffect, useCallback, Fragment } from 'react';
import { BarChart3, Eye, X, Search, FileText, Loader2, Printer, Check, CheckCircle, XCircle, MinusCircle, Users, ChevronDown, TrendingDown, TrendingUp } from 'lucide-react';
import api from '../../../services/api';
import { scoreColor, avatarGradient, SORT_MODES, SORT_ORDER, groupHistory } from '../ScopeLanding/resultats/helpers';
import HistoryView from '../ScopeLanding/resultats/HistoryView';
import StatsView from '../ScopeLanding/resultats/StatsView';

/**
 * Suivi des apprenants de l'espace formateur, sur le modèle du « Suivi des résultats » d'une thématique
 * partagée (TabResultats) : indicateurs de cohorte, filtres, cartes apprenants, puis au clic l'historique
 * (frise / classement, séances groupées) et les statistiques (tendance, couverture, points forts et
 * faibles). En plus : revue d'une séance question par question, attestations pour plusieurs apprenants, et
 * groupes libres (promo, classe…) : les indicateurs et la synthèse du groupe suivent le groupe choisi.
 * Données : formateur/cohort.php (liste), qcm/history.php et user/stats.php (target_user_id, branche
 * formateur), formateur/session-review.php, formateur/attestation.php (student_ids),
 * formateur/student-group.php (rangement), formateur/group-stats.php (synthèse du groupe).
 */

const packLabel = (label) => {
    if (!label) return '';
    const parts = String(label).split(' · ');
    return parts.length >= 3 ? parts.slice(1).join(' · ') : label;
};

const renderDonut = (pct) => {
    const C = 2 * Math.PI * 33;
    const has = pct != null;
    const col = has ? scoreColor(pct) : '#cbd5e1';
    return (
        <svg width="50" height="50" viewBox="0 0 80 80" style={{ flex: 'none' }}>
            <circle cx="40" cy="40" r="33" fill="none" stroke={has ? `${col}33` : '#eef1f7'} strokeWidth="7" />
            <circle cx="40" cy="40" r="33" fill="none" stroke={col} strokeWidth="7" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={has ? C * (1 - pct / 100) : C} transform="rotate(-90 40 40)" />
            <text x="40" y="46" textAnchor="middle" fontFamily="var(--font-display)" fontWeight="800" fontSize={has ? 17 : 15} fill={col}>{has ? `${pct}%` : '—'}</text>
        </svg>
    );
};

const NONE = '__none__';

export default function FormateurTracking({ cohorts }) {
    // Groupes modifiés dans cette page (clé code-apprenant → nom), en attendant le prochain chargement.
    const [groupEdits, setGroupEdits] = useState({});
    // Une ligne par (code, apprenant) : un apprenant peut avoir activé deux codes du même formateur.
    const rows = useMemo(() => cohorts.flatMap(c => (c.students || []).map(s => {
        const key = `${c.code_id}-${s.user_id}`;
        return {
            ...s, key, code_id: c.code_id, code: c.code,
            scope_id: c.scope_id, scope_label: c.scope_label, codeLabel: packLabel(c.label) || c.code,
            grp: (key in groupEdits ? groupEdits[key] : s.group) || '',
        };
    })), [cohorts, groupEdits]);
    const scopes = useMemo(() => [...new Map(rows.map(r => [r.scope_id, r.scope_label])).entries()], [rows]);
    const groups = useMemo(() => [...new Map(cohorts.map(c => [c.code_id, { id: c.code_id, label: `${packLabel(c.label) || c.code}`, scope_id: c.scope_id }])).values()], [cohorts]);

    const [scopeFilter, setScopeFilter] = useState(scopes.length === 1 ? scopes[0][0] : 0);
    const [groupFilter, setGroupFilter] = useState(0);
    const [personFilter, setPersonFilter] = useState('all');
    const [personSort, setPersonSort] = useState('score');
    const [search, setSearch] = useState('');
    const [openKey, setOpenKey] = useState(null);
    const [sub, setSub] = useState('historique');
    const [hist, setHist] = useState([]);
    const [histLoading, setHistLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [histFilter, setHistFilter] = useState('all');
    const [histSort, setHistSort] = useState('recent');
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());
    const [review, setReview] = useState(null); // { historyId, studentId, scopeId, name }
    const [selected, setSelected] = useState(() => new Set()); // clés (code-apprenant) pour l'attestation
    const [showAttest, setShowAttest] = useState(false);
    const [grpFilter, setGrpFilter] = useState(''); // '' = tous, NONE = sans groupe, sinon le nom du groupe
    const [showGroupDlg, setShowGroupDlg] = useState(false);
    const [synth, setSynth] = useState(null);
    const [synthOpen, setSynthOpen] = useState(true);
    const [synthVersion, setSynthVersion] = useState(0);

    const toggleGroup = useCallback((g) => setExpandedGroups(prev => { const n = new Set(prev); n.has(g) ? n.delete(g) : n.add(g); return n; }), []);

    const scopeRows = rows.filter(r => !scopeFilter || r.scope_id === scopeFilter);
    const grpNames = [...new Set(scopeRows.map(r => r.grp).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
    const allGrpNames = [...new Set(rows.map(r => r.grp).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
    const activeGrp = grpFilter && grpFilter !== NONE && !grpNames.includes(grpFilter) ? '' : grpFilter;
    const inScope = scopeRows.filter(r => (!groupFilter || r.code_id === groupFilter)
        && (!activeGrp || (activeGrp === NONE ? !r.grp : r.grp === activeGrp)));
    const synthScope = scopeFilter || (scopes.length === 1 ? scopes[0][0] : 0);
    const grpTitle = activeGrp === NONE ? 'Sans groupe' : activeGrp || (grpNames.length ? 'Tous les groupes' : 'Tous les apprenants');
    const nbPersonnes = inScope.length;
    const nbActives = inScope.filter(u => (u.quiz_completed || 0) > 0).length;
    const rated = inScope.filter(u => u.success_rate != null);
    const scoreMoyen = rated.length ? Math.round(rated.reduce((s, u) => s + u.success_rate, 0) / rated.length) : 0;
    const nbQuiz = inScope.reduce((s, u) => s + (u.quiz_completed || 0), 0);
    const q = search.trim().toLowerCase();
    const list = inScope.filter(u => {
        const engaged = (u.quiz_completed || 0) > 0;
        if (personFilter === 'active' && !engaged) return false;
        if (personFilter === 'inactive' && engaged) return false;
        if (q && !((u.prenom || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))) return false;
        return true;
    }).sort(SORT_MODES[personSort].cmp);
    const sort = SORT_MODES[personSort];

    const open = (u) => {
        setOpenKey(u.key);
        setSub('historique');
        setHist([]); setStats(null);
        setHistLoading(true); setStatsLoading(true);
        api.get('/qcm/history.php', { params: { scope_id: u.scope_id, target_user_id: u.user_id } })
            .then(({ data }) => setHist(data.data || [])).catch(() => setHist([])).finally(() => setHistLoading(false));
        api.get('/user/stats.php', { params: { scope_id: u.scope_id, target_user_id: u.user_id } })
            .then(({ data }) => setStats(data.data || null)).catch(() => setStats(null)).finally(() => setStatsLoading(false));
    };

    // Synthèse du groupe (catégories, points faibles / forts) : une thématique précise, hors filtre par code.
    useEffect(() => {
        if (!synthScope || groupFilter) { setSynth(null); return; }
        let alive = true;
        setSynth('loading');
        api.get('/formateur/group-stats.php', { params: { scope_id: synthScope, ...(activeGrp ? { group: activeGrp } : {}) } })
            .then(({ data }) => { if (alive) setSynth(data.data || null); })
            .catch(() => { if (alive) setSynth(null); });
        return () => { alive = false; };
    }, [synthScope, activeGrp, groupFilter, synthVersion]);

    const saveGroup = async (name) => {
        const items = rows.filter(r => selected.has(r.key)).map(r => ({ code_id: r.code_id, student_id: r.user_id }));
        await api.post('/formateur/student-group.php', { items, group: name });
        setGroupEdits(prev => { const n = { ...prev }; rows.forEach(r => { if (selected.has(r.key)) n[r.key] = name || ''; }); return n; });
        setSelected(new Set());
        setSynthVersion(v => v + 1);
    };

    const toggleSel = (key) => setSelected(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const selectedRows = rows.filter(r => selected.has(r.key));
    const selScopes = [...new Set(selectedRows.map(r => r.scope_id))];

    if (!rows.length) {
        return (
            <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', padding: '20px 0' }}>
                Aucun apprenant pour l’instant : distribuez vos codes (onglet « Mes codes »), les résultats apparaîtront ici dès qu’un apprenant aura activé son code.
            </div>
        );
    }

    return (
        <div className="sl-resultats ftrack" style={{ '--hist-accent': 'var(--primary, #7c3aed)' }}>
            <div className="track-kpis">
                <div className="stats-card"><div className="stats-card__num">{nbPersonnes}</div><div className="stats-card__lbl">Apprenants</div></div>
                <div className="stats-card"><div className="stats-card__num" style={{ color: '#10b981' }}>{nbActives}</div><div className="stats-card__lbl">Avec activité</div></div>
                <div className="stats-card"><div className="stats-card__num">{scoreMoyen}%</div><div className="stats-card__lbl">Score moyen</div></div>
                <div className="stats-card"><div className="stats-card__num">{nbQuiz}</div><div className="stats-card__lbl">Quiz réalisés</div></div>
            </div>

            {scopes.length > 1 && (
                <div className="track-groups">
                    <span className="track-groups__lbl">Thématique :</span>
                    <button type="button" className={`hist-filter ${!scopeFilter ? 'is-active' : ''}`} onClick={() => { setScopeFilter(0); setGroupFilter(0); }}>Toutes</button>
                    {scopes.map(([id, label]) => (
                        <button key={id} type="button" className={`hist-filter ${scopeFilter === id ? 'is-active' : ''}`} onClick={() => { setScopeFilter(id); setGroupFilter(0); }}>{label}</button>
                    ))}
                </div>
            )}
            {groups.filter(g => !scopeFilter || g.scope_id === scopeFilter).length > 1 && (
                <div className="track-groups">
                    <span className="track-groups__lbl">Code :</span>
                    <button type="button" className={`hist-filter ${!groupFilter ? 'is-active' : ''}`} onClick={() => setGroupFilter(0)}>Tous</button>
                    {groups.filter(g => !scopeFilter || g.scope_id === scopeFilter).map(g => (
                        <button key={g.id} type="button" className={`hist-filter ${groupFilter === g.id ? 'is-active' : ''}`} onClick={() => setGroupFilter(groupFilter === g.id ? 0 : g.id)}>{g.label}</button>
                    ))}
                </div>
            )}

            {grpNames.length > 0 && (
                <div className="track-groups">
                    <span className="track-groups__lbl">Groupe :</span>
                    <button type="button" className={`hist-filter ${!activeGrp ? 'is-active' : ''}`} onClick={() => setGrpFilter('')}>Tous</button>
                    {grpNames.map(g => (
                        <button key={g} type="button" className={`hist-filter ${activeGrp === g ? 'is-active' : ''}`} onClick={() => setGrpFilter(activeGrp === g ? '' : g)}>
                            {g} ({scopeRows.filter(r => r.grp === g).length})
                        </button>
                    ))}
                    {scopeRows.some(r => !r.grp) && (
                        <button type="button" className={`hist-filter ${activeGrp === NONE ? 'is-active' : ''}`} onClick={() => setGrpFilter(activeGrp === NONE ? '' : NONE)}>
                            Sans groupe ({scopeRows.filter(r => !r.grp).length})
                        </button>
                    )}
                </div>
            )}

            {synthScope > 0 && !groupFilter && synth && synth !== 'loading' && synth.sessions > 0 && (
                <div className="stats-block ftrack-synth">
                    <button type="button" className="ftrack-synth__head" onClick={() => setSynthOpen(o => !o)}>
                        <Users size={16} />
                        <span className="stats-block__title">Synthèse · {grpTitle}</span>
                        <span className="ftrack-synth__meta">
                            {synth.active_students} apprenant{synth.active_students > 1 ? 's' : ''} avec activité · {synth.sessions} quiz
                            {scopes.length > 1 ? ` · ${scopes.find(s => s[0] === synthScope)?.[1] || ''}` : ''}
                        </span>
                        <ChevronDown size={16} className={`ftrack-synth__chev ${synthOpen ? 'is-open' : ''}`} />
                    </button>
                    {synthOpen && <GroupSynthesis synth={synth} />}
                </div>
            )}

            <div className="track-filters-row">
                <div className="hist-filters">
                    <button type="button" className={`hist-filter ${personFilter === 'all' ? 'is-active' : ''}`} onClick={() => setPersonFilter('all')}>Tous ({nbPersonnes})</button>
                    <button type="button" className={`hist-filter ${personFilter === 'active' ? 'is-active' : ''}`} onClick={() => setPersonFilter('active')}>Activité débutée ({nbActives})</button>
                    <button type="button" className={`hist-filter ${personFilter === 'inactive' ? 'is-active' : ''}`} onClick={() => setPersonFilter('inactive')}>Sans activité ({nbPersonnes - nbActives})</button>
                </div>
                <button type="button" className="track-sort" onClick={() => setPersonSort(s => SORT_ORDER[(SORT_ORDER.indexOf(s) + 1) % SORT_ORDER.length])} title="Changer le tri">
                    Trier : <b>{sort.label} {sort.arrow}</b>
                </button>
            </div>
            <div className="ftrack-tools">
                <label className="ftrack-search">
                    <Search size={14} />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un apprenant (nom ou email)" />
                </label>
                <button type="button" className="btn btn-sm btn-ghost"
                    onClick={() => setSelected(prev => (list.length && list.every(r => prev.has(r.key)) ? new Set() : new Set(list.map(r => r.key))))}>
                    {list.length && list.every(r => selected.has(r.key)) ? 'Tout décocher' : 'Tout cocher'}
                </button>
                <button type="button" className="btn btn-sm btn-secondary" disabled={!selected.size}
                    onClick={() => setShowGroupDlg(true)} title="Cochez des apprenants pour les ranger dans un groupe">
                    <Users size={14} /> Groupe{selected.size ? ` (${selected.size})` : ''}
                </button>
                <button type="button" className="btn btn-sm btn-secondary" disabled={!selected.size}
                    onClick={() => setShowAttest(true)} title="Cochez des apprenants d’une même thématique">
                    <FileText size={14} /> Attestations{selected.size ? ` (${selected.size})` : ''}
                </button>
            </div>

            <div className="track-list">
                {list.length === 0 ? (
                    <p style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>Aucun apprenant ne correspond.</p>
                ) : list.map(su => {
                    const engaged = (su.quiz_completed || 0) > 0;
                    const isOpen = openKey === su.key;
                    const name = su.prenom || su.email;
                    const initial = (name || '?').trim().charAt(0).toUpperCase();
                    const lastActDate = su.last_activity ? new Date(String(su.last_activity).replace(' ', 'T')) : null;
                    const isToday = lastActDate && lastActDate.toDateString() === new Date().toDateString();
                    const lastAct = lastActDate ? `dernière activité ${isToday ? "aujourd'hui" : lastActDate.toLocaleDateString('fr-FR')}` : 'jamais commencé';
                    const accessEnd = su.access_end ? new Date(String(su.access_end).replace(' ', 'T')).toLocaleDateString('fr-FR') : null;
                    return (
                        <Fragment key={su.key}>
                            <div className={`track-card ${!engaged ? 'track-card--inactive' : ''} ${isOpen ? 'track-card--open' : ''}`}
                                onClick={() => (isOpen ? setOpenKey(null) : open(su))}>
                                <input type="checkbox" className="ftrack-check" checked={selected.has(su.key)}
                                    onClick={e => e.stopPropagation()} onChange={() => toggleSel(su.key)} aria-label={`Sélectionner ${name}`} />
                                {renderDonut(su.success_rate)}
                                <span className="track-card__avatar" style={{ background: engaged ? avatarGradient(name) : '#e2e8f0', color: engaged ? '#fff' : '#94a3b8' }}>{initial}</span>
                                <div className="track-card__info">
                                    <div className="track-card__nameline">
                                        <span className="track-card__name">{name}</span>
                                        <span className={`track-badge ${engaged ? 'track-badge--active' : 'track-badge--inactive'}`}>{engaged ? 'Activité débutée' : 'Sans activité'}</span>
                                        {allGrpNames.length > 0 && <span className={`track-card__group ${su.grp ? '' : 'ftrack-nogroup'}`}>{su.grp || 'Sans groupe'}</span>}
                                        {groups.length > 1 && <span className="track-card__group ftrack-codechip">{su.codeLabel}</span>}
                                        {!su.is_active && <span className="track-card__expired">accès expiré</span>}
                                    </div>
                                    <div className="track-card__email">{su.email}</div>
                                    <div className="track-card__meta">
                                        {scopes.length > 1 ? `${su.scope_label} · ` : ''}{accessEnd ? `accès jusqu’au ${accessEnd} · ` : ''}{lastAct}
                                    </div>
                                </div>
                                <div className="track-card__quiz">
                                    <div className="track-card__quiz-n">{su.quiz_completed} quiz</div>
                                    {engaged && <div className="track-card__quiz-sub">{su.total_answers} réponses</div>}
                                </div>
                                <button type="button" className="track-card__btn" onClick={e => { e.stopPropagation(); isOpen ? setOpenKey(null) : open(su); }}>
                                    {isOpen ? <><X size={14} /> Fermer</> : <><Eye size={14} /> Voir</>}
                                </button>
                            </div>
                            {isOpen && (
                                <div className="sl-tracking-inline ftrack-detail">
                                    <div className="hist-switch">
                                        <button type="button" className={`hist-switch__card ${sub === 'historique' ? 'is-active' : ''}`} onClick={() => setSub('historique')}>
                                            <span className="hist-switch__num">01</span>
                                            <span className="hist-switch__body">
                                                <span className="hist-switch__title">Historique</span>
                                                <span className="hist-switch__sub">{groupHistory(hist).length} quiz terminé{groupHistory(hist).length > 1 ? 's' : ''} · revue des séances</span>
                                            </span>
                                        </button>
                                        <button type="button" className={`hist-switch__card ${sub === 'stats' ? 'is-active' : ''}`} onClick={() => setSub('stats')}>
                                            <span className="hist-switch__num">02</span>
                                            <span className="hist-switch__body">
                                                <span className="hist-switch__title">Statistiques</span>
                                                <span className="hist-switch__sub">Score, points forts et faibles</span>
                                            </span>
                                        </button>
                                    </div>
                                    {sub === 'historique' && (histLoading ? <div className="loading">Chargement…</div>
                                        : hist.length === 0 ? (
                                            <div className="sl-empty-state"><span className="sl-empty-icon"><ContentIllustration name="clipboard-list" size={64} /></span><p>Aucun quiz terminé pour le moment.</p></div>
                                        ) : (
                                            <HistoryView entries={hist} filter={histFilter} setFilter={setHistFilter} sort={histSort} setSort={setHistSort}
                                                readOnly onClear={null} expandedGroups={expandedGroups} toggleGroup={toggleGroup}
                                                onConsulter={() => {}} onRefaire={() => {}}
                                                onReview={(entry) => setReview({ historyId: entry.id, studentId: su.user_id, scopeId: su.scope_id, name })} />
                                        ))}
                                    {sub === 'stats' && (statsLoading ? <div className="loading">Chargement…</div>
                                        : !stats ? <div className="sl-empty-state"><span className="sl-empty-icon"><BarChart3 size={40} strokeWidth={1.5} /></span><p>Aucune donnée disponible.</p></div>
                                        : <StatsView stats={stats} />)}
                                </div>
                            )}
                        </Fragment>
                    );
                })}
            </div>

            {review && <SessionReviewModal {...review} onClose={() => setReview(null)} />}
            {showGroupDlg && (
                <GroupDialog count={selected.size} names={allGrpNames} onSave={saveGroup} onClose={() => setShowGroupDlg(false)} />
            )}
            {showAttest && (
                <AttestationDialog rows={selectedRows} scopes={selScopes} onClose={() => setShowAttest(false)} />
            )}
        </div>
    );
}

/** Revue d'une séance d'un apprenant : chaque question, sa réponse, la bonne réponse et l'explication. */
function SessionReviewModal({ historyId, studentId, scopeId, name, onClose }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [onlyWrong, setOnlyWrong] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get('/formateur/session-review.php', { params: { history_id: historyId, student_id: studentId, scope_id: scopeId } })
            .then(({ data: res }) => { if (alive) setData(res.data); })
            .catch(e => { if (alive) setError(e.response?.data?.message || 'Séance indisponible'); });
        return () => { alive = false; };
    }, [historyId, studentId, scopeId]);
    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose]);

    const qs = data?.questions || [];
    const wrong = qs.filter(q => q.given !== q.bonne_reponse);
    const shown = onlyWrong ? wrong : qs;
    const s = data?.session;
    const pct = s && s.total > 0 ? Math.round(s.score / s.total * 100) : null;
    const html = (t) => ({ __html: String(t || '').replace(/\n\n/g, '<br><br>') });

    return (
        <div className="frev-overlay" onClick={onClose} style={{ '--hist-accent': 'var(--primary, #7c3aed)' }}>
            <div className="frev" onClick={e => e.stopPropagation()} role="dialog" aria-label="Revue de séance">
                <header className="frev__head">
                    <div>
                        <strong>{s ? s.label : 'Revue de séance'}</strong>
                        <span>{name}{s ? ` · ${new Date(String(s.created_at).replace(' ', 'T')).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}{pct != null ? ` · ${s.score}/${s.total} (${pct} %)` : ''}</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
                </header>
                {data && qs.length > 0 && (
                    <div className="frev__bar">
                        <div className="hist-filters">
                            <button type="button" className={`hist-filter ${!onlyWrong ? 'is-active' : ''}`} onClick={() => setOnlyWrong(false)}>Toutes ({qs.length})</button>
                            <button type="button" className={`hist-filter ${onlyWrong ? 'is-active' : ''}`} onClick={() => setOnlyWrong(true)}>À revoir ({wrong.length})</button>
                        </div>
                    </div>
                )}
                <div className="frev__body">
                    {error ? <div className="alert alert-error">{error}</div>
                        : !data ? <div style={{ color: 'var(--text-muted)' }}><Loader2 size={16} className="spin" /> Chargement…</div>
                        : shown.length === 0 ? <p style={{ color: 'var(--text-muted)' }}>{onlyWrong ? 'Aucune erreur sur cette séance.' : 'Questions indisponibles (retirées depuis).'}</p>
                        : shown.map((q, i) => {
                            const ok = q.given === q.bonne_reponse;
                            const none = q.given == null;
                            return (
                                <article key={q.id} className={`frev-q ${ok ? 'is-ok' : 'is-ko'}`}>
                                    <div className="frev-q__head">
                                        {ok ? <CheckCircle size={16} /> : none ? <MinusCircle size={16} /> : <XCircle size={16} />}
                                        <span>{ok ? 'Bonne réponse' : none ? 'Sans réponse' : 'Erreur'}</span>
                                        <em>Question {qs.indexOf(q) + 1}</em>
                                    </div>
                                    <p className="frev-q__enonce" dangerouslySetInnerHTML={html(q.enonce)} />
                                    <ul className="frev-q__reps">
                                        {q.reponses.map(r => {
                                            const isGood = r.id === q.bonne_reponse;
                                            const isGiven = r.id === q.given;
                                            return (
                                                <li key={r.id} className={`${isGood ? 'is-good' : ''} ${isGiven && !isGood ? 'is-wrong' : ''}`}>
                                                    <span className="frev-q__mark">{isGood ? <Check size={13} /> : isGiven ? <X size={13} /> : null}</span>
                                                    <span dangerouslySetInnerHTML={html(r.text)} />
                                                    {isGiven && <small>réponse de l’apprenant</small>}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    {q.explication && <div className="frev-q__expl" dangerouslySetInnerHTML={html(q.explication)} />}
                                </article>
                            );
                        })}
                </div>
            </div>
        </div>
    );
}

/** Attestations pour les apprenants cochés (une même thématique ; une page par apprenant à l'impression). */
function AttestationDialog({ rows, scopes, onClose }) {
    const [org, setOrg] = useState(() => { try { return localStorage.getItem('mq_formateur_org') || ''; } catch { return ''; } });
    const [trainer, setTrainer] = useState(() => { try { return localStorage.getItem('mq_formateur_trainer') || ''; } catch { return ''; } });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const sameScope = scopes.length === 1;
    const ids = [...new Set(rows.map(r => r.user_id))];

    const generate = async () => {
        setBusy(true); setError(null);
        try {
            try { localStorage.setItem('mq_formateur_org', org); localStorage.setItem('mq_formateur_trainer', trainer); } catch { /* ignore */ }
            const res = await api.get('/formateur/attestation.php', {
                params: { student_ids: ids.join(','), scope_id: scopes[0], org, trainer },
                responseType: 'text', transformResponse: [(d) => d],
            });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'text/html' }));
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            onClose();
        } catch (e) {
            setError("Les attestations n’ont pas pu être générées.");
        }
        setBusy(false);
    };

    return (
        <div className="frev-overlay" onClick={onClose} style={{ '--hist-accent': 'var(--primary, #7c3aed)' }}>
            <div className="frev frev--small" onClick={e => e.stopPropagation()} role="dialog" aria-label="Attestations">
                <header className="frev__head">
                    <div>
                        <strong>Attestations de résultats</strong>
                        <span>{ids.length} apprenant{ids.length > 1 ? 's' : ''} · une page par apprenant, à imprimer ou enregistrer en PDF</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
                </header>
                <div className="frev__body">
                    {!sameScope ? (
                        <p className="alert alert-error">Les apprenants cochés suivent des thématiques différentes : cochez des apprenants d’une seule thématique (une attestation porte sur une thématique).</p>
                    ) : (
                        <>
                            <p className="ftrack-attest__who">{rows.slice(0, 6).map(r => r.prenom || r.email).join(', ')}{rows.length > 6 ? '…' : ''}</p>
                            <label className="ftrack-field"><span>Organisme</span><input value={org} onChange={e => setOrg(e.target.value)} placeholder="Nom de votre organisme" /></label>
                            <label className="ftrack-field"><span>Formateur</span><input value={trainer} onChange={e => setTrainer(e.target.value)} placeholder="Votre nom" /></label>
                            {error && <p className="alert alert-error">{error}</p>}
                            <button type="button" className="btn btn-primary" onClick={generate} disabled={busy}>
                                {busy ? <Loader2 size={15} className="spin" /> : <Printer size={15} />} Générer les attestations
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Synthèse d'un groupe : maîtrise par catégorie et sujets les plus faibles / les plus forts. */
function GroupSynthesis({ synth }) {
    const cats = [...(synth.by_category || [])].sort((a, b) => b.avg_score - a.avg_score);
    const catColor = (v) => v >= 70 ? '#10b981' : '#f97316';
    const sfList = (items) => items.map(it => (
        <div key={it.id} className="stats-sf__row">
            <span className="stats-sf__label">{it.label}<small className="ftrack-synth__n"> · {it.students} apprenant{it.students > 1 ? 's' : ''}</small></span>
            <span className="stats-sf__val" style={{ color: scoreColor(it.avg_score) }}>{it.avg_score}%</span>
        </div>
    ));
    return (
        <div className="ftrack-synth__body stats-v2">
            {cats.length > 0 && (
                <div className="stats-catbars">
                    {cats.map(c => (
                        <div key={c.id} className="stats-catbar" title={`${c.students} apprenant(s), ${c.sessions} quiz`}>
                            <span className="stats-catbar__label" title={c.label}>{c.label}</span>
                            <div className="stats-catbar__track"><div className="stats-catbar__fill" style={{ width: `${Math.min(100, c.avg_score)}%`, background: catColor(c.avg_score) }} /></div>
                            <span className="stats-catbar__val" style={{ color: catColor(c.avg_score) }}>{c.avg_score}%</span>
                        </div>
                    ))}
                </div>
            )}
            {(synth.weakest?.length > 0 || synth.strongest?.length > 0) && (
                <div className="stats-cols">
                    {synth.weakest?.length > 0 && (
                        <div className="stats-block">
                            <div className="stats-sf__head"><span className="stats-sf__icon stats-sf__icon--weak"><TrendingDown size={14} /></span><span className="stats-block__title">Sujets à retravailler</span></div>
                            <div className="stats-sf__list">{sfList(synth.weakest)}</div>
                        </div>
                    )}
                    {synth.strongest?.length > 0 && (
                        <div className="stats-block">
                            <div className="stats-sf__head"><span className="stats-sf__icon stats-sf__icon--strong"><TrendingUp size={14} /></span><span className="stats-block__title">Sujets réussis</span></div>
                            <div className="stats-sf__list">{sfList(synth.strongest)}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/** Ranger les apprenants cochés dans un groupe existant, un nouveau groupe, ou « Sans groupe ». */
function GroupDialog({ count, names, onSave, onClose }) {
    const [value, setValue] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const save = async (name) => {
        setBusy(true); setError(null);
        try { await onSave(name.trim()); onClose(); }
        catch (e) { setError(e.response?.data?.message || 'Le groupe n’a pas pu être enregistré.'); setBusy(false); }
    };
    return (
        <div className="frev-overlay" onClick={onClose} style={{ '--hist-accent': 'var(--primary, #7c3aed)' }}>
            <div className="frev frev--small" onClick={e => e.stopPropagation()} role="dialog" aria-label="Groupe">
                <header className="frev__head">
                    <div>
                        <strong>Ranger dans un groupe</strong>
                        <span>{count} apprenant{count > 1 ? 's' : ''} coché{count > 1 ? 's' : ''} · promo, classe, session…</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
                </header>
                <div className="frev__body">
                    {names.length > 0 && (
                        <div className="hist-filters">
                            {names.map(n => <button key={n} type="button" className="hist-filter" disabled={busy} onClick={() => save(n)}>{n}</button>)}
                        </div>
                    )}
                    <form className="ftrack-grpnew" onSubmit={e => { e.preventDefault(); if (value.trim()) save(value); }}>
                        <label className="ftrack-field"><span>{names.length ? 'Ou un nouveau groupe' : 'Nom du groupe'}</span>
                            <input value={value} maxLength={60} onChange={e => setValue(e.target.value)} placeholder="Ex. Promo septembre 2026" autoFocus />
                        </label>
                        <button type="submit" className="btn btn-primary" disabled={busy || !value.trim()}>
                            {busy ? <Loader2 size={15} className="spin" /> : <Check size={15} />} Enregistrer
                        </button>
                    </form>
                    {error && <p className="alert alert-error">{error}</p>}
                    <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => save('')}>Retirer du groupe (Sans groupe)</button>
                </div>
            </div>
        </div>
    );
}
